'use client'
import {
  isSeminarOpen,
  toSeminarStatusTag,
  toSeminarTitle
} from '@/components/cms/templates/SeminarListSection'
import { ArticleLayout } from '@/components/layouts/article-layout'
import { ActionButton } from '@/components/parts/action-button'
import { NewtBreadcrumbs } from '@/components/parts/breadcrumbs/NewtBreadcrumbs'
import { Contact } from '@/components/parts/contact'
import { CaseCard, toCaseCardImage } from '@/components/parts/case-card'
import { Chip } from '@/components/parts/chip'
import { PreviousAndNextPanel } from '@/components/parts/previous-and-next'
import { RailCard } from '@/components/parts/rail-card'
import { RelatedSection } from '@/components/parts/related-section'
import SeminarForm from '@/components/parts/seminar-form'
import { useSyncExternalStore } from 'react'
import type { Department } from '@/types/microcms/department'
import type { ContentBlock, Seminar } from '@/types/microcms/seminar'
import { getOptimizedImageUrl } from '@/utils/image'

type Props = {
  seminar: Seminar
  nextSeminar: Seminar | null
  prevSeminar: Seminar | null
  /** 「その他のセミナー」に並べる 2 件（現在のセミナーを除く） */
  relatedSeminars: Seminar[]
  /**
   * サーバーで描画した時刻（ms）。受付状態の判定に使う。
   * 静的書き出しの HTML はビルド時刻で判定されるため、hydration はこの時刻で揃え、
   * その直後にブラウザの現在時刻で判定し直す。省略時はブラウザの時刻のみで判定する（プレビュー用）
   */
  renderedAt?: number
  departments: Department[]
}

/** 本文中の CMS 画像を webp に変換し、遅延読み込みにする */
const CMS_IMAGE =
  /<img([^>]*)src=['"](https:\/\/(?:images\.microcms-assets\.io|fraim\.assets\.newt\.so)\/[^'"]+)['"]/gi

const optimizeBodyImages = (html: string) =>
  html.replace(CMS_IMAGE, (_match, attrs, src) => {
    const optimizedSrc = getOptimizedImageUrl(src, {
      width: 800,
      quality: 80,
      format: 'webp'
    })
    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
  })

/** 旧 CMS（NEWT）から移したセミナーの本文ブロックを microCMS の形に揃える */
const toMicroCMSBlock = (block: ContentBlock): ContentBlock => {
  if (block.fieldId) return block
  const { type, data } = block
  switch (type) {
    case 'RICH_TEXT':
      return { fieldId: 'text', text: data }
    case 'smalltext':
      return { fieldId: 'smalltext', text: data?.text }
    case 'flexbox':
      return {
        fieldId: 'flexbox',
        title: data?.title,
        explanation: data?.explanation,
        thumbnail: { url: data?.thumbnail?.src, alt: data?.thumbnail?.altText }
      }
    case 'button':
      return { fieldId: 'button' }
    default:
      return { fieldId: '' }
  }
}

/** 本文ブロックは配列のほか、JSON 文字列で入っていることがある */
const parseContents = (contents: Seminar['contents']): ContentBlock[] => {
  if (!contents) return []
  const blocks = typeof contents === 'string' ? JSON.parse(contents) : contents
  return Array.isArray(blocks) ? blocks.map(toMicroCMSBlock) : []
}

const scrollToSignup = () => {
  document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })
}

/** 本文の 1 ブロック。text はリッチテキスト、flexbox は画像＋説明の横並び */
const ContentBlockView = ({
  block,
  open
}: {
  block: ContentBlock
  open: boolean
}) => {
  switch (block.fieldId) {
    case 'text':
      return (
        <div
          className="resource-body"
          dangerouslySetInnerHTML={{
            __html: block.text ? optimizeBodyImages(block.text) : ''
          }}
        />
      )
    case 'smalltext':
      return (
        <div
          className="text-sm leading-compact text-ink"
          dangerouslySetInnerHTML={{ __html: block.text || '' }}
        />
      )
    case 'flexbox': {
      const thumbnailSrc = block.thumbnail?.url
        ? getOptimizedImageUrl(block.thumbnail.url, {
            width: 400,
            quality: 80,
            format: 'webp'
          })
        : ''
      return (
        <div className="flex flex-col gap-4">
          {block.title ? (
            <h2 className="fontfeaturesettings text-2xl font-bold tracking-heading text-ink">
              {block.title}
            </h2>
          ) : null}
          <div className="flex flex-col items-start gap-6 md:flex-row">
            {thumbnailSrc ? (
              <img
                src={thumbnailSrc}
                alt={block.thumbnail?.alt || ''}
                className="w-full rounded-xl md:max-w-52"
                loading="lazy"
              />
            ) : null}
            <div
              className="resource-body min-w-0 flex-1"
              dangerouslySetInnerHTML={{ __html: block.explanation || '' }}
            />
          </div>
        </div>
      )
    }
    case 'button':
      // SP ではフォームが本文の下に回るので、そこまで送るボタンを出す（受付中のみ）
      return open ? (
        <ActionButton
          label={block.text || 'お申し込みはこちら'}
          block
          className="lg:hidden"
          onClick={scrollToSignup}
        />
      ) : null
    default:
      return null
  }
}

/**
 * 右カラム。Figma: rail。
 * 受付中は申込フォーム、終了後は資料があればダウンロードフォーム、無ければ受付終了の告知。
 */
const SeminarRail = ({
  seminar,
  departments,
  open
}: {
  seminar: Seminar
  departments: Department[]
  open: boolean
}) => {
  if (open) {
    return (
      <RailCard id="signup" title="お申し込みはこちら">
        <SeminarForm seminar={seminar} departments={departments} />
      </RailCard>
    )
  }

  if (seminar.contactDlc) {
    return (
      <RailCard
        id="signup"
        title={
          <>
            セミナーの
            <wbr />
            資料ダウンロード
            <wbr />
            はこちら
          </>
        }
        lead="資料のダウンロードURLを送信いたします"
      >
        <SeminarForm seminar={seminar} departments={departments} />
      </RailCard>
    )
  }

  // Figma: 受付終了のカードには扉＋%・& キャラのイラストが乗る
  return (
    <RailCard
      id="signup"
      illustSrc="/images/illust/seminar/rail01.svg"
      title={
        <>
          お申し込みは
          <wbr />
          終了しました
        </>
      }
    />
  )
}

/**
 * 判定に使うブラウザの時刻。getSnapshot は毎回同じ値を返す必要があるため変数に持ち、
 * マウント時（subscribe）に取り直す。React は subscribe 後に値の変化を検知して描画し直す。
 * チャンクはプリフェッチで早めに読み込まれるので、読み込み時刻のままだと古くなる
 */
let clientNow = Date.now()
const refreshClientNow = () => {
  clientNow = Date.now()
  return () => {}
}

/** hydration 中は serverNow、以降はブラウザの時刻を返す */
const useNow = (serverNow?: number) =>
  useSyncExternalStore(
    refreshClientNow,
    () => clientNow,
    () => serverNow ?? clientNow
  )

/**
 * セミナーの詳細。Figma: （PC）セミナー詳細。
 * 本文（740）と右カラム（420）の 2 カラム。/service/[id] と同じ構成で、
 * lg では余白を詰めて右カラムを 320 に縮める。
 */
export function SeminarDetail({
  seminar,
  nextSeminar,
  prevSeminar,
  relatedSeminars,
  departments,
  renderedAt
}: Props) {
  const now = useNow(renderedAt)
  const open = isSeminarOpen(seminar, now)
  const title = toSeminarTitle(seminar)
  const blocks = parseContents(seminar.contents)

  return (
    <>
      <ArticleLayout
        main={
          <article className="flex min-w-0 flex-1 flex-col gap-14">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-6">
                <h1 className="fontfeaturesettings text-2xl font-bold leading-heading tracking-heading text-ink lg:text-h2">
                  {title}
                </h1>
                <div className="flex items-center gap-3">
                  <Chip
                    label={open ? 'お申込み受付中' : '受付終了'}
                    surface={open ? 'beige' : 'muted'}
                  />
                </div>
              </div>
              {/*
                Figma のメイン画像（740×416）には eyecatch を出さない。
                既存のセミナーは本文の先頭に同じバナーを入れて運用しており、出すと二重になるため、
                本文先頭の画像をメイン画像として扱う（eyecatch は一覧カードと OGP で使う）
              */}
              {seminar.body ? (
                <div
                  className="resource-body"
                  dangerouslySetInnerHTML={{ __html: seminar.body }}
                />
              ) : null}
              {blocks.map((block, index) => (
                <ContentBlockView
                  key={`${block.fieldId}-${index}`}
                  block={block}
                  open={open}
                />
              ))}
            </div>
            <PreviousAndNextPanel
              previous={
                prevSeminar
                  ? {
                      href: `/seminar/${prevSeminar.id}/`,
                      title: toSeminarTitle(prevSeminar)
                    }
                  : undefined
              }
              next={
                nextSeminar
                  ? {
                      href: `/seminar/${nextSeminar.id}/`,
                      title: toSeminarTitle(nextSeminar)
                    }
                  : undefined
              }
              allHref="/seminar/"
            />
          </article>
        }
        aside={
          <SeminarRail
            seminar={seminar}
            departments={departments}
            open={open}
          />
        }
      />
      {relatedSeminars.length > 0 ? (
        <RelatedSection title="その他のセミナー" columns={2}>
          {relatedSeminars.map((item) => (
            <CaseCard
              key={item.id}
              href={`/seminar/${item.id}/`}
              title={toSeminarTitle(item)}
              clampTitle
              image={toCaseCardImage(item.eyecatch)}
              tags={[toSeminarStatusTag(item, 'white', now)]}
            />
          ))}
        </RelatedSection>
      ) : null}
      {/* CTA の下にパンくずを出す（layout の Footer はこのページでは CTA を出さない） */}
      <Contact />
      <NewtBreadcrumbs contentType="seminar" title={title} />
    </>
  )
}
